import { createContext, useContext, useEffect, useState, useCallback, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useProfileState } from '@/shared/hooks/useProfileState';
import { useEntitlements } from '@/shared/hooks/useEntitlements';
import { useAdminStatus } from '@/shared/hooks/useAdminStatus';
import { usePresence } from '@/shared/hooks/usePresence';
import { useAuthBootstrap } from '@/features/auth/hooks/useAuthBootstrap';
import { useDeviceTrust } from '@/features/auth/hooks/useDeviceTrust';
import { useAuthHydration } from '@/features/auth/hooks/useAuthHydration';
import { performSignOut } from '@/features/auth/services/authSignOut';
import { handleSignedOutTransition } from '@/features/auth/services/authTransitions';
import type { AuthContextType } from '@/features/auth/types';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [privateKey, setPrivateKey] = useState<CryptoKey | null>(null);

  const { profile, fetchProfile, resetProfile } = useProfileState();
  const { credits, allocatedCredits, creditsLoaded, isPremium, fetchCredits, reset: resetEntitlements } = useEntitlements();
  const { isAdmin, fetchAdminStatus, reset: resetAdmin } = useAdminStatus();
  const { isDeviceTrusted, evaluateAndSet, refreshDeviceTrust, reset: resetDeviceTrust } = useDeviceTrust();

  const resetAll = useCallback(async () => {
    await handleSignedOutTransition({
      resetProfile,
      resetAdmin,
      resetDeviceTrust,
      resetEntitlements,
      setPrivateKey,
    });
  }, [resetProfile, resetAdmin, resetDeviceTrust, resetEntitlements]);

  const { hydrateUser } = useAuthHydration({
    fetchProfile,
    fetchCredits,
    fetchAdminStatus,
    setPrivateKey,
    evaluateDeviceTrust: evaluateAndSet,
    setIsAuthReady: (ready: boolean) => {
      setIsAuthReadyExt(ready);
    },
  });

  // We need a way to set isAuthReady from hydration
  const [isAuthReadyExt, setIsAuthReadyExt] = useState<boolean | null>(null);

  const { session, user, loading, isAuthReady: bootstrapAuthReady, setIsAuthReady } = useAuthBootstrap({
    onHydrate: hydrateUser,
    onSignedOut: resetAll,
  });

  // Sync external isAuthReady from hydration
  useEffect(() => {
    if (isAuthReadyExt !== null) {
      setIsAuthReady(isAuthReadyExt);
      setIsAuthReadyExt(null);
    }
  }, [isAuthReadyExt, setIsAuthReady]);

  const isAuthReady = bootstrapAuthReady;

  usePresence(user);

  // Refresh admin status on focus/visibility
  useEffect(() => {
    if (!user) return;
    const refreshAdmin = () => fetchAdminStatus(user.id);
    refreshAdmin();
    window.addEventListener('focus', refreshAdmin);
    const handleVisibilityChange = () => { if (!document.hidden) refreshAdmin(); };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      window.removeEventListener('focus', refreshAdmin);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [user, fetchAdminStatus]);

  const signUp = async (email: string, password: string, displayName: string, referralCode?: string) => {
    const metadata: Record<string, string> = { display_name: displayName };
    if (referralCode) metadata.referral_code = referralCode;
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: { emailRedirectTo: window.location.origin, data: metadata },
    });
    if (error) throw error;
  };

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
  };

  const signOut = async () => {
    await performSignOut(user?.id);
  };

  const refreshProfile = async () => {
    if (user) await fetchProfile(user.id);
  };

  const refreshCredits = async () => {
    if (user) await fetchCredits(user.id);
  };

  const handleRefreshDeviceTrust = async () => {
    if (user) await refreshDeviceTrust(user.id);
  };

  return (
    <AuthContext.Provider value={{
      session, user, profile, loading, isAuthReady, isAdmin, privateKey,
      credits, allocatedCredits, creditsLoaded, isPremium,
      isDeviceTrusted, refreshDeviceTrust: handleRefreshDeviceTrust,
      signUp, signIn, signOut, refreshProfile, refreshCredits,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
}
